<template>
  <div class="quarantine">
    <div class="quarantine-header">
      <h2>Quarantine</h2>
      <div class="admins-section">
        <span class="admins-label">Admins</span>
        <span v-if="!isAdminsEditable" class="edit-icon" title="Edit admin emails" @click="isAdminsEditable = true">&#9998;</span>
        <span v-else class="save-icon" title="Save admin emails" @click="saveAdmins">&#10003;</span>
        <template v-if="!isAdminsEditable">
          <span v-if="!adminEmails.length" class="admins-empty"></span>
          <ul v-else class="email-list">
            <li v-for="email in adminEmails" :key="email" class="email-list-item">{{ email }}</li>
          </ul>
        </template>
        <p-tags v-else v-model="adminEmails" placeholder="Add admin email" :options="suggestAdmins" />
      </div>
    </div>

    <div v-if="loading" class="quarantine-empty">Loading…</div>
    <div v-else-if="error" class="quarantine-error">{{ error }}</div>
    <div v-else-if="!entries.length" class="quarantine-empty">No quarantined emails.</div>

    <template v-else>
      <ul class="quarantine-list">
        <li class="quarantine-item header">
          <div>Subject</div>
          <div>Sender</div>
          <div>Received</div>
          <div>Conflicting Clients</div>
          <div>References</div>
          <div>Release</div>
        </li>
        <li
          v-for="entry in entries"
          :key="entry.id"
          class="quarantine-item"
          :class="{ 'is-saved': entry.saved, 'is-released': entry.released }"
        >
          <div class="cell-subject" :title="entry.subject">{{ entry.subject || '(no subject)' }}</div>
          <div class="cell-sender">{{ entry.sender }}</div>
          <div>{{ formatDate(entry.receivedAt) }}</div>
          <div class="cell-orgs">
            <template v-if="editingOrgsId === entry.id">
              <p-tags
                :value="entry.conflictingOrgs.map(o => o.name)"
                placeholder=""
                :options="[]"
                :disabled="entry.released"
                @input="onOrgsChange(entry, $event)"
              />
              <p-button
                color="primary"
                variant="text"
                title="Done"
                :disabled="savingId === entry.id"
                @click="saveAndClose(entry)"
              >&#10003;</p-button>
            </template>
            <template v-else>
              <ul class="tag-list">
                <li v-for="org in entry.conflictingOrgs" :key="org.name">{{ org.name }}</li>
              </ul>
              <p-button
                v-if="!entry.released"
                variant="text"
                title="Edit conflicting orgs"
                class="edit-btn"
                @click="editingOrgsId = entry.id"
              >&#9998;</p-button>
            </template>
          </div>
          <div class="cell-refs">
            <template v-if="editingId === entry.id">
              <p-tags
                v-model="entry.references"
                placeholder="Add reference"
                :options="entry.suggestedReferences || []"
                :disabled="entry.released"
                @input="onRefsChange(entry)"
              />
              <p-button
                color="primary"
                variant="text"
                title="Save references"
                :disabled="savingId === entry.id"
                @click="saveAndClose(entry)"
              >&#10003;</p-button>
            </template>
            <template v-else>
              <ul class="tag-list">
                <li v-for="ref in entry.references" :key="ref">{{ ref }}</li>
              </ul>
              <p-button
                v-if="!entry.released"
                variant="text"
                title="Edit references"
                class="edit-btn"
                @click="editingId = entry.id"
              >&#9998;</p-button>
            </template>
            <p-button
              color="primary"
              variant="text"
              title="Download email"
              class="download-btn"
              :disabled="entry.released"
              @click="download(entry)"
            >⤓</p-button>
          </div>
          <div class="cell-action">
            <p-button
              color="primary"
              variant="text"
              title="Release email"
              :disabled="entry.released || releasingId === entry.id"
              @click="release(entry)"
            >Release</p-button>
          </div>
        </li>
      </ul>
      <div class="pagination">
        <p-button variant="text" :disabled="page === 0" @click="goToPage(page - 1)">&#8249;</p-button>
        <span>{{ page + 1 }} / {{ totalPages }}</span>
        <p-button variant="text" :disabled="page >= totalPages - 1" @click="goToPage(page + 1)">&#8250;</p-button>
      </div>
    </template>
  </div>
</template>

<script>
import httpClient from '@/utils/httpClient';
import Button from '@/components/common/Button';
import Tags from '@/components/common/Tags';

const BASE = '/api/hub-correspondence/quarantine';
const PAGE_SIZE = 20;

export default {
  components: {
    'p-button': Button,
    'p-tags': Tags
  },
  data() {
    return {
      entries: [],
      total: 0,
      page: 0,
      adminEmails: [],
      isAdminsEditable: false,
      loading: false,
      error: null,
      savingId: null,
      releasingId: null,
      editingId: null,
      editingOrgsId: null
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / PAGE_SIZE));
    }
  },
  async created() {
    await Promise.all([this.load(), this.loadSettings()]);
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data, total } = await httpClient.get(`${BASE}?skip=${this.page * PAGE_SIZE}&size=${PAGE_SIZE}`);
        this.total = total;
        this.entries = (data || []).map((e) => ({
          ...e,
          released: e.status === 'released',
          references: e.referencesEdited ? e.references : (e.references?.length ? e.references : [...(e.suggestedReferences || [])]),
          saved: false
        }));
      } catch {
        this.error = 'Failed to load quarantine list.';
      } finally {
        this.loading = false;
      }
    },

    async goToPage(p) {
      this.page = p;
      await this.load();
    },

    async loadSettings() {
      try {
        const data = await httpClient.get(`${BASE}/settings`);
        this.adminEmails = data?.adminEmails || [];
      } catch {
      }
    },

    suggestAdmins(q) {
      if (!q) return [];
      return httpClient.get('/api/auth/suggest/users?q=' + encodeURIComponent(q));
    },

    async saveAdmins() {
      try {
        await httpClient.put(`${BASE}/settings`, { adminEmails: this.adminEmails });
        this.isAdminsEditable = false;
      } catch {
        this.$toast?.error({ title: 'Failed to save admin emails' });
      }
    },

    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleString();
    },

    onOrgsChange(entry, newOrgNames) {
      const removed = entry.conflictingOrgs
        .filter(o => !newOrgNames.includes(o.name));

      const removedKeys = new Set(
        removed.flatMap(o => [
          o.name.toLowerCase(),
          ...o.aliases.map(a => a.toLowerCase()),
        ])
      );

      if (removedKeys.size) {
        entry.references = entry.references.filter(ref => {
          const prefix = ref.split(':')[0]?.toLowerCase();
          return !removedKeys.has(prefix);
        });
      }

      entry.conflictingOrgs = entry.conflictingOrgs.filter(o => newOrgNames.includes(o.name));
      entry.saved = false;
    },

    onRefsChange(entry) {
      const remainingPrefixes = new Set(
        entry.references.map(ref => ref.split(':')[0]?.toLowerCase())
      );
      entry.conflictingOrgs = entry.conflictingOrgs.filter(org => {
        const keys = [org.name.toLowerCase(), ...org.aliases.map(a => a.toLowerCase())];
        return keys.some(k => remainingPrefixes.has(k));
      });
      entry.saved = false;
    },

    async saveAndClose(entry) {
      this.savingId = entry.id;
      try {
        await httpClient.patch(`${BASE}/${entry.id}/references`, {
          references: entry.references,
          conflictingOrgs: entry.conflictingOrgs,
        });
        entry.saved = true;
        entry.referencesEdited = true;
        this.editingId = null;
        this.editingOrgsId = null;
      } catch {
        this.$toast?.error({ title: 'Failed to save references' });
      } finally {
        this.savingId = null;
      }
    },

    buildFilename(entry) {
      const date = entry.receivedAt
        ? new Date(entry.receivedAt).toISOString().slice(0, 19).replace('T', ' ').replace(/:/g, '_')
        : 'unknown';
      const sender = entry.sender || '';
      const subject = entry.subject || '';
      return `${date} ${sender} (${subject})`.replace(/[^a-z0-9\-_ ()]/gi, '_').substring(0, 200) + '.eml';
    },

    async download(entry) {
      try {
        const content = await httpClient.get(`${BASE}/${entry.id}/download`);
        const blob = new Blob([content], { type: 'message/rfc822' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.buildFilename(entry);
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        this.$toast?.error({ title: 'Failed to download email' });
      }
    },

    async release(entry) {
      if (entry.released) return;

      if (!entry.referencesEdited) {
        const confirmed = await this.$confirm({
          title: 'References not reviewed',
          message: 'You have not reviewed the references for this email.\n\nAll suggested references will be applied automatically on release.',
          confirm: 'Release'
        });
        if (!confirmed) return;
      }

      this.releasingId = entry.id;
      try {
        await httpClient.post(`${BASE}/${entry.id}/release`);
        await this.load();
      } catch {
        this.$toast?.error({ title: 'Failed to release email' });
      } finally {
        this.releasingId = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.quarantine {
  margin-top: 2rem;
  width: 75%;

  .quarantine-header {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;

    h2 { margin: 0; }

    .admins-section {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      flex: 1;

      ::v-deep .vue-tags-input {
        min-width: 280px;

        .ti-autocomplete {
          min-width: 280px;
          white-space: nowrap;
        }
      }

      .admins-label {
        font-size: 0.8rem;
        color: var(--theme-on-background-accent);
        font-weight: 600;
        white-space: nowrap;
      }

      .edit-icon, .save-icon {
        cursor: pointer;
        font-size: 0.85rem;
        color: var(--theme-primary);
        &:hover { opacity: 0.7; }
      }

      .admins-empty {
        font-size: 0.8rem;
        color: var(--theme-on-surface-dark);
      }

      .email-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;

        .email-list-item {
          background: var(--theme-highlight);
          border-radius: 3px;
          padding: 0.1rem 0.4rem;
          font-size: 0.75rem;
        }
      }
    }
  }

  .quarantine-empty {
    color: var(--theme-on-surface-dark);
    font-size: 0.85rem;
    font-style: italic;
  }

  .quarantine-error {
    color: var(--theme-error);
    font-size: 0.85rem;
  }

  .quarantine-list {
    list-style: none;
    margin: 0;
    padding: 0;

    .quarantine-item {
      display: grid;
      grid-template-columns: minmax(120px, 280px) minmax(120px, 220px) 150px minmax(80px, 130px) minmax(100px, 1fr) 90px;
      border-bottom: 1px solid var(--theme-highlight);
      font-size: 0.75rem;
      transition: background-color 0.2s;

      > * {
        padding: 0.4rem 0.5rem;
        border-left: 1px solid var(--theme-highlight);

        &:last-child {
          border-right: 1px solid var(--theme-highlight);
        }
      }

      &:first-child {
        border-top: 1px solid var(--theme-highlight);
      }

      &.header {
        color: var(--theme-on-background-accent);
        font-weight: 600;

        > * {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding: 0.25rem 0.5rem;
        }
      }

      &:not(.header):not(.is-released):hover {
        background-color: var(--theme-highlight);

        .edit-btn { opacity: 1; }
      }

      &.is-saved:not(.is-released) {
        background-color: rgba(var(--theme-primary-rgb, 76, 175, 80), 0.06);
        border-left: 2px solid var(--theme-primary);
      }

      &.is-released {
        opacity: 0.45;
      }

      .cell-orgs {
        display: flex;
        align-items: center;
        gap: 0.3rem;

        .tag-list { flex: 1; }

        .edit-btn {
          opacity: 0;
          flex-shrink: 0;
          margin-left: auto;
        }

        ::v-deep .vue-tags-input .ti-new-tag-input-wrapper { display: none; }
      }

      .cell-subject {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .cell-sender {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .cell-action {
        display: flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
      }

      .cell-refs {
        display: flex;
        align-items: center;
        gap: 0.3rem;

        .tag-list { flex: 1; }

        .edit-btn {
          opacity: 0;
          flex-shrink: 0;
        }

        .download-btn {
          flex-shrink: 0;
          margin-left: auto;
          opacity: 0;
        }
      }

      &:not(.header):hover .cell-refs .download-btn { opacity: 1; }

      .tag-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.15rem;

        li {
          background: var(--theme-highlight);
          border-radius: 3px;
          padding: 0.1rem 0.3rem;
        }
      }
    }
  }

  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
    font-size: 0.8rem;
    color: var(--theme-on-surface);
  }
}
</style>
